import FlussonicMsePlayer from "@flussonic/flussonic-mse-player";
import * as Sentry from "@sentry/browser";
import { diagnosticEventLog } from "../../../shared/services/diagnostic";
import { serializeFlussonicError } from "../flussonicErrorSerializer";
import {
  buildPlayerFailureTags,
  getPlayerFailureDiagnostics,
} from "../playerNetworkDiagnostics";

/**
 * MSEPlayerManager - Manages the lifecycle of a Flussonic MSE Player instance
 *
 * This class ensures proper cleanup and prevents the common InvalidStateError
 * that occurs when SourceBuffer is accessed after being removed from MediaSource.
 *
 * Key principles:
 * 1. Always stop the player BEFORE manipulating MediaSource
 * 2. Track state to prevent operations on destroyed instances
 * 3. Let the player manage its own MediaSource lifecycle
 * 4. Mark as connected immediately when play() resolves
 */
class MSEPlayerManager {
  // Configurable disconnect delay - increased to ensure WebSocket fully closes
  static DISCONNECT_DELAY_MS = 500; // Increased from 300ms

  // Number of consecutive mse-no-data-errors before we ask the parent for a
  // fresh <audio> element. WebKit (Safari) sometimes wedges an audio element's
  // MSE pipeline irrecoverably after extended hidden-tab periods, and even a
  // proper detach (pause + removeAttribute('src') + load()) doesn't unstick it.
  // Swapping the DOM element gives a guaranteed clean slate.
  static FRESH_ELEMENT_THRESHOLD = 2;

  // Number of consecutive codec-mismatch / decode failures before we ask the
  // parent for a fresh <video> element. A codec mismatch is normally a
  // transient server-side stream reset (presenter/channel switchover, encoder
  // restart) that self-heals on the very next reconnect, so the first couple of
  // occurrences are free fast reconnects against the same element. Only after
  // repeated failures do we pay for the heavier DOM swap, which forces a brand
  // new MediaSource so the SourceBuffer is re-negotiated against the current
  // (settled) stream codec. Higher than FRESH_ELEMENT_THRESHOLD because a
  // single retry clears this far more often than the WebKit no-data wedge.
  static CODEC_MISMATCH_FRESH_ELEMENT_THRESHOLD = 3;

  // Transient errors (Flussonic WebSocket disconnects, stall timeouts) usually
  // recover on the very next reconnect — the network blip clears or the edge
  // failover kicks in. Marking the first occurrence within this window as
  // _skipRetryCount = true prevents one isolated blip from burning a slot in
  // the 10-retry budget. Repeated occurrences inside the window still count,
  // so a genuinely broken connection still escalates to the fallback chain.
  static TRANSIENT_ERROR_WINDOW_MS = 60000;

  constructor() {
    this.player = null;
    this.mediaElement = null;
    this.state = "idle"; // idle | connecting | connected | stopping | destroyed
    this.address = null;
    this.callbacks = {
      onSuccess: null,
      onError: null,
      onRequestFreshElement: null,
    };
    this.disconnectPromise = null; // Track ongoing disconnect operation
    this.connectionId = 0; // Unique ID for each connection attempt
    this.activeConnectionId = null; // Track which connection is currently active
    this.bufferTrimInterval = null; // Periodic buffer cleanup for long sessions
    this._stallTimeout = null; // Stall detection timeout
    this._lastStallReconnectTime = 0; // Cooldown tracker for stall-triggered reconnects
    this._handleExternalPause = null; // Listener for browser power-saving pauses
    this._consecutiveNoDataErrors = 0; // Tracks repeated no-data failures (Issue 17A)
    this._consecutiveCodecMismatches = 0; // Tracks repeated codec/decode resets (Issue 13K)
    this._lastWsErrorAt = 0; // Tracks last flussonic-ws-error for transient-window check
    this._lastStallErrorAt = 0; // Tracks last stall-timeout-reconnect for transient-window check
  }

  /**
   * Check if MSE is supported in the current browser
   */
  static isSupported() {
    return FlussonicMsePlayer.isSupported();
  }

  /**
   * Classify whether a (serialized) error message represents a recoverable
   * server-side stream reset — a codec mismatch or a decode-pipeline failure.
   *
   * These are NOT client bugs: Flussonic restarts/re-muxes the stream
   * (presenter or broadcasting-machine switchover, encoder restart) and the
   * audio chunks no longer match the SourceBuffer the player negotiated. The
   * fix is an immediate clean reconnect, not a retry-budget penalty.
   *
   * Message-only by design so it can run against BOTH the raw Flussonic error
   * and the message we synthesize later from `mediaElement.error` — the
   * MEDIA_ERR_DECODE variant only carries the codec text after enrichment.
   *
   * @param {string} message
   * @returns {{ isCodecMismatch: boolean, isDecodeFailure: boolean }}
   */
  static classifyRecoverableStreamError(message) {
    const msg = message || "";
    const isCodecMismatch =
      msg.includes("CHUNK_DEMUXER_ERROR_APPEND_FAILED") && msg.includes("codec");
    const isDecodeFailure =
      msg.includes("PIPELINE_ERROR_DECODE") ||
      msg.includes("Failed to send video packet for decoding");
    return { isCodecMismatch, isDecodeFailure };
  }

  /**
   * Get current player state
   */
  getState() {
    return this.state;
  }

  /**
   * Check if player is active (connecting or connected)
   */
  isActive() {
    return this.state === "connecting" || this.state === "connected";
  }


  /**
   * Start MSE connection
   * @param {Object} options - Connection options
   * @param {HTMLVideoElement} options.audioElement - Video element for MSE
   * @param {string} options.address - Stream address
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Error callback
   */
  async connect({ audioElement, address, onSuccess, onError, onRequestFreshElement }) {
    // Generate unique connection ID for this attempt
    this.connectionId++;
    const thisConnectionId = this.connectionId;

    console.log(`[MSE Connection #${thisConnectionId}] Starting connection to ${address}`);

    // Wait for any pending disconnect to complete first
    if (this.disconnectPromise) {
      console.log(`[MSE Connection #${thisConnectionId}] Waiting for previous disconnect to complete...`);
      try {
        await this.disconnectPromise;
      } catch (e) {
        // Ignore disconnect errors
        console.debug(`[MSE Connection #${thisConnectionId}] Previous disconnect failed:`, e);
      }
    }

    // After waiting for disconnect, check if we still need to clean up
    // (Only if state isn't already idle/destroyed and player exists)
    if (this.player && this.state !== "idle" && this.state !== "destroyed") {
      console.log(`[MSE Connection #${thisConnectionId}] Cleaning up existing player (state: ${this.state})`);
      await this._internalDisconnect();
    }

    // Final check - if we're in a bad state, reset to idle
    if (this.state === "stopping") {
      console.log(`[MSE Connection #${thisConnectionId}] Resetting from 'stopping' to 'idle'`);
      this.state = "idle";
    }

    this.state = "connecting";
    this.mediaElement = audioElement;
    this.address = address;
    this.callbacks = { onSuccess, onError, onRequestFreshElement };
    this.activeConnectionId = thisConnectionId; // Mark this as the active connection

    console.log(`[MSE Connection #${thisConnectionId}] Set as active connection`);

    try {
      if (!audioElement) {
        throw new Error("Audio element not found");
      }

      // Create player with proper callbacks
      this.player = new FlussonicMsePlayer(audioElement, address + "/mse_ld?", {
        debug: true,
        video: false,
        audio: true,
        statsSendEnable: false,

        // Stall detection: if a stall doesn't resolve within 4s, proactively reconnect
        // before the MediaSource crashes (~5s based on live testing).
        // 30s cooldown prevents rapid cycling on bad networks.
        onStartStalling: () => {
          if (this.activeConnectionId !== thisConnectionId) return;
          if (Date.now() - this._lastStallReconnectTime < 30000) return;

          console.log(`[MSE Connection #${thisConnectionId}] Stalling detected`);
          this._stallTimeout = setTimeout(() => {
            if (this.state !== "connected" || this.activeConnectionId !== thisConnectionId) return;
            console.warn(`[MSE Connection #${thisConnectionId}] Stall lasted >4s — proactive reconnect`);
            const now = Date.now();
            const isFirstInWindow =
              now - this._lastStallErrorAt > MSEPlayerManager.TRANSIENT_ERROR_WINDOW_MS;
            this._lastStallErrorAt = now;
            this._lastStallReconnectTime = now;
            diagnosticEventLog.record("mse:audio", "warn", "stall-timeout-reconnect", {
              connectionId: thisConnectionId,
              skipRetryCount: isFirstInWindow,
            });
            if (onError) {
              const stallErr = new Error("MSE stall timeout — reconnecting");
              if (isFirstInWindow) stallErr._skipRetryCount = true;
              onError(stallErr);
            }
            this.disconnect();
          }, 4000);
        },

        onEndStalling: () => {
          if (this._stallTimeout) {
            clearTimeout(this._stallTimeout);
            this._stallTimeout = null;
          }
        },

        onError: (err) => {
          // Ignore errors from stale connections
          if (this.activeConnectionId !== thisConnectionId) {
            console.log(`[MSE Connection #${thisConnectionId}] Ignoring error from stale connection:`, err);
            diagnosticEventLog.record("mse:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "error" });
            return;
          }

          // play_promise_reject is Flussonic's wrapper for AbortError on play() — expected during reconnection
          if (err && typeof err === "object" && err.error === "play_promise_reject") {
            console.debug(`[MSE Connection #${thisConnectionId}] play() interrupted (Flussonic play_promise_reject) — expected`);
            diagnosticEventLog.record("mse:audio", "info", "play-interrupted", { connectionId: thisConnectionId });
            return;
          }

          // Flussonic command/WebSocket errors — transient network event, player will reconnect.
          // First occurrence within TRANSIENT_ERROR_WINDOW_MS doesn't burn the retry budget;
          // repeated ones inside the window do (so a genuinely broken connection still falls back).
          if (err && typeof err === "object" && err.command === "error") {
            const now = Date.now();
            const isFirstInWindow =
              now - this._lastWsErrorAt > MSEPlayerManager.TRANSIENT_ERROR_WINDOW_MS;
            this._lastWsErrorAt = now;
            console.warn(`[MSE Connection #${thisConnectionId}] Flussonic WebSocket error (transient):`, err.msg);
            diagnosticEventLog.record("mse:audio", "warn", "flussonic-ws-error", {
              connectionId: thisConnectionId,
              msg: String(err.msg),
              skipRetryCount: isFirstInWindow,
            });
            if (this.isActive() && onError) {
              const wsErr = new Error("Flussonic WebSocket error (transient)");
              if (isFirstInWindow) wsErr._skipRetryCount = true;
              onError(wsErr);
            }
            this.disconnect();
            return;
          }

          // AbortError during teardown (MediaSource detached, fetch cancelled, etc.)
          if (err instanceof DOMException && err.name === "AbortError") {
            console.debug(`[MSE Connection #${thisConnectionId}] AbortError (teardown artifact) — ignoring`);
            diagnosticEventLog.record("mse:audio", "info", "abort-error-teardown", { connectionId: thisConnectionId });
            return;
          }

          // Codec mismatch or decode failure — server-side stream reset, not a client bug.
          // Immediate clean reconnect without counting against the retry limit.
          // This is the RAW path: some Flussonic errors carry the codec text
          // directly. The MEDIA_ERR_DECODE variant does NOT — it's caught by the
          // post-enrichment gate further down (after mediaElement.error is read).
          {
            const { message: checkMsg } = serializeFlussonicError(err);
            const { isCodecMismatch, isDecodeFailure } =
              MSEPlayerManager.classifyRecoverableStreamError(checkMsg);

            if (isCodecMismatch || isDecodeFailure) {
              this._handleRecoverableStreamError({
                errorKind: isCodecMismatch ? "codec_mismatch" : "decode_failure",
                message: checkMsg,
                connectionId: thisConnectionId,
                address,
                onError,
                enriched: false,
              });
              return;
            }
          }

          console.error(`[MSE Connection #${thisConnectionId}] MSE Player error:`, err);

          let { message: errMsg, details: errDetails } = serializeFlussonicError(err);

          // Snapshot the media element state for diagnostics
          const mediaSnapshot = this._snapshotMediaState();

          // Flussonic sometimes passes "" or bare Error() to onError — enrich from media element
          const isUnhelpfulError = errMsg === "Empty string error" || errMsg.includes("no useful message") || errMsg === "Unknown Flussonic error (no error data)";
          if (isUnhelpfulError) {
            if (this.mediaElement?.error) {
              const mediaErr = serializeFlussonicError(this.mediaElement.error);
              errMsg = `MSE media error: ${mediaErr.message}`;
              errDetails = { ...errDetails, ...mediaErr.details, originalErr: String(err) };
            } else {
              // No MediaError = connection failed before streaming started. Reconnect, skip Sentry.
              const snapshot = this._snapshotMediaState();
              this._consecutiveNoDataErrors += 1;
              const consecutive = this._consecutiveNoDataErrors;
              console.warn(
                `[MSE Connection #${thisConnectionId}] MSE connection failed before data received` +
                  ` (consecutive=${consecutive}):`,
                snapshot
              );
              diagnosticEventLog.record("mse:audio", "warn", "mse-no-data-error", {
                connectionId: thisConnectionId,
                consecutiveNoDataCount: consecutive,
                ...snapshot,
                originalErr: String(err),
              });

              // Stubborn-Safari escape: after FRESH_ELEMENT_THRESHOLD consecutive
              // no-data errors on the same audio element, ask the parent for a
              // fresh DOM element. WebKit's MSE pipeline can wedge irrecoverably
              // and only a new <audio> element gets us back to a clean state.
              if (
                consecutive >= MSEPlayerManager.FRESH_ELEMENT_THRESHOLD &&
                typeof this.callbacks.onRequestFreshElement === "function"
              ) {
                diagnosticEventLog.record("mse:audio", "warn", "fresh-element-requested", {
                  connectionId: thisConnectionId,
                  consecutiveNoDataCount: consecutive,
                });
                Sentry.addBreadcrumb({
                  category: "mse",
                  level: "warning",
                  message: "Requesting fresh audio element after consecutive no-data errors",
                  data: { connectionId: thisConnectionId, consecutiveNoDataCount: consecutive },
                });
                try {
                  this.callbacks.onRequestFreshElement();
                } catch (cbErr) {
                  console.warn(
                    `[MSE Connection #${thisConnectionId}] onRequestFreshElement threw:`,
                    cbErr
                  );
                }
                // Reset so we don't immediately re-request on the next try.
                this._consecutiveNoDataErrors = 0;
              }

              if (this.isActive() && onError) {
                onError(new Error("MSE connection failed (no data received)"));
              }
              this.disconnect();
              return;
            }
            errDetails.mediaSnapshot = mediaSnapshot;
          }

          // Post-enrichment recoverable gate. The MEDIA_ERR_DECODE codec
          // mismatch (Issue NEWSQUAWK-PORTALS-13K) only carries its codec text
          // AFTER errMsg is synthesized from mediaElement.error above, so the
          // raw-path check upstream could not see it. Re-classify on the
          // enriched message so it self-heals (immediate clean reconnect, no
          // retry-budget penalty, breadcrumb only) instead of burning the
          // retry budget and firing a Sentry issue on every server-side reset.
          {
            const { isCodecMismatch, isDecodeFailure } =
              MSEPlayerManager.classifyRecoverableStreamError(errMsg);
            if (isCodecMismatch || isDecodeFailure) {
              this._handleRecoverableStreamError({
                errorKind: isCodecMismatch ? "codec_mismatch" : "decode_failure",
                message: errMsg,
                connectionId: thisConnectionId,
                address,
                onError,
                enriched: true,
              });
              return;
            }
          }

          const onErrorDiagnostics = getPlayerFailureDiagnostics(
            address,
            "mse",
            errMsg,
            mediaSnapshot
          );
          Sentry.captureException(new Error(errMsg), {
            tags: {
              ...buildPlayerFailureTags(
                "MSEPlayerManager",
                "mse_connection_error",
                "mse",
                onErrorDiagnostics
              ),
              player_state: this.state,
              network_state: mediaSnapshot.networkState,
              ready_state: mediaSnapshot.readyState,
              media_paused: String(mediaSnapshot.paused),
            },
            extra: {
              address,
              errorDetails: errDetails,
              connectionId: thisConnectionId,
              mediaSnapshot,
              networkDiagnostics: onErrorDiagnostics.network,
            },
            fingerprint: ["MSEPlayerManager", "onError", errMsg],
          });

          // Only callback if we're still in an active state
          if (this.isActive() && onError) {
            onError(new Error(errMsg));
          }

          // Trigger cleanup
          this.disconnect();
        },

        onDisconnect: (status) => {
          // Ignore disconnects from stale connections
          if (this.activeConnectionId !== thisConnectionId) {
            console.log(`[MSE Connection #${thisConnectionId}] Ignoring disconnect from stale connection:`, status);
            diagnosticEventLog.record("mse:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "disconnect" });
            return;
          }

          console.log(`[MSE Connection #${thisConnectionId}] MSE Player disconnected:`, status);

          // Only treat as error if we were already connected
          // During initial connection, disconnect events are part of normal setup
          if (this.state === "connected") {
            const mediaSnapshot = this._snapshotMediaState();
            diagnosticEventLog.record("mse:audio", "error", "unexpected-disconnect", { connectionId: thisConnectionId, address, status, mediaSnapshot });
            Sentry.captureException(new Error(`MSE Player disconnected: ${status}`), {
              tags: {
                component: "MSEPlayerManager",
                context: "mse_unexpected_disconnect",
                connection_type: "mse",
                network_state: mediaSnapshot.networkState,
                ready_state: mediaSnapshot.readyState,
              },
              extra: {
                address,
                status,
                connectionId: thisConnectionId,
                mediaSnapshot,
              },
              fingerprint: ["MSEPlayerManager", "onDisconnect"],
            });

            if (onError) {
              onError(new Error(`MSE Player state ${status}`));
            }

            // Trigger cleanup
            this.disconnect();
          }

          // If we're still connecting, ignore disconnect events - they're part of normal setup
          // The 'playing' event or fallback timeout will determine success
        },
      });

      // Disable stats
      this.player.statsSendEnable = false;

      // Disable the library's "on crash, retry video-only" recovery. On a
      // play()-promise rejection (autoplay block / AbortError during reconnect)
      // it reads `mediaInfo.activeStreams` unguarded and throws "Cannot read
      // properties of undefined (reading 'activeStreams')" when mediaInfo isn't
      // populated yet (Sentry NEWSQUAWK-PORTALS-S7 — top crash, ~1.7k events).
      // Passing onCrashTryVideoOnly:false in opts is ineffective: the library
      // resolves it as `opts.x || DEFAULT` with DEFAULT=true, so false is
      // coerced back to true. It reads opts live when the crash path fires, so
      // override on the instance here. We stream audio-only, so a video-only
      // retry is meaningless regardless.
      if (this.player.opts) {
        this.player.opts.onCrashTryVideoOnly = false;
      }

      // Attempt to play
      await this.player.play();

      // Only mark as connected if this is still the active connection
      // This prevents stale connection promises from corrupting state
      if (this.activeConnectionId !== thisConnectionId) {
        console.log(`[MSE Connection #${thisConnectionId}] Ignoring success from stale connection`);
        diagnosticEventLog.record("mse:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "success" });
        return;
      }

      // If play() succeeded, mark as connected immediately
      if (this.state === "connecting") {
        console.log(`[MSE Connection #${thisConnectionId}] Connection successful (play resolved)`);
        this.state = "connected";
        // Bytes are flowing again; clear failure trackers so a transient hiccup
        // minutes later doesn't carry forward and trigger an element swap or
        // retry-budget escalation unnecessarily.
        this._consecutiveNoDataErrors = 0;
        this._consecutiveCodecMismatches = 0;
        this._lastWsErrorAt = 0;
        this._lastStallErrorAt = 0;
        this._startBufferTrimming();
        this._startExternalPauseDetection(audioElement, thisConnectionId);
        if (this.callbacks.onSuccess) {
          this.callbacks.onSuccess(this.player);
        }
      }
    } catch (err) {
      // Only handle error if this is still the active connection
      if (this.activeConnectionId !== thisConnectionId) {
        console.log(`[MSE Connection #${thisConnectionId}] Ignoring catch error from stale connection:`, err);
        diagnosticEventLog.record("mse:audio", "warn", "stale-event-ignored", { connectionId: thisConnectionId, eventType: "catch" });
        return;
      }

      // AbortError from play() interruption during reconnection is expected — not a real error
      if (err instanceof DOMException && err.name === "AbortError") {
        console.debug(`[MSE Connection #${thisConnectionId}] play() interrupted (AbortError) — expected during reconnection`);
        diagnosticEventLog.record("mse:audio", "info", "play-interrupted", { connectionId: thisConnectionId });
        await this.disconnect();
        return;
      }

      const { message: errorMessage, details: errorDetails } = serializeFlussonicError(err);
      const mediaSnapshot = this._snapshotMediaState();

      console.error(`[MSE Connection #${thisConnectionId}] MSE connection failed:`, errorMessage);

      const catchDiagnostics = getPlayerFailureDiagnostics(
        address,
        "mse",
        errorMessage,
        mediaSnapshot
      );
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          ...buildPlayerFailureTags(
            "MSEPlayerManager",
            "mse_connection_catch_error",
            "mse",
            catchDiagnostics
          ),
          network_state: mediaSnapshot.networkState,
          ready_state: mediaSnapshot.readyState,
        },
        extra: {
          address,
          errorDetails,
          connectionId: thisConnectionId,
          mediaSnapshot,
          networkDiagnostics: catchDiagnostics.network,
        },
        fingerprint: ["MSEPlayerManager", "catch", errorMessage],
      });

      // Clean up on error
      await this.disconnect();

      if (onError) {
        onError(new Error(errorMessage));
      }
    }
  }

  /**
   * Shared handler for a recoverable codec-mismatch / decode-failure (a
   * server-side stream reset, not a client bug). Called from both the raw
   * onError path and the post-enrichment gate.
   *
   * Behaviour: breadcrumb only (NEVER captureException — that drowned Issue
   * 13K with successful auto-recoveries), track consecutive occurrences, and
   * after CODEC_MISMATCH_FRESH_ELEMENT_THRESHOLD consecutive resets escalate
   * to a fresh-element rebuild (a bare reconnect keeps re-negotiating the same
   * wedged SourceBuffer). Always finishes with an immediate clean reconnect
   * that does NOT count against the retry budget (`_skipRetryCount`).
   *
   * @param {object} opts
   * @param {"codec_mismatch"|"decode_failure"} opts.errorKind
   * @param {string} opts.message - serialized error message
   * @param {number} opts.connectionId
   * @param {string} opts.address
   * @param {Function} opts.onError
   * @param {boolean} opts.enriched - true if message came from mediaElement.error enrichment
   */
  _handleRecoverableStreamError({ errorKind, message, connectionId, address, onError, enriched }) {
    this._consecutiveCodecMismatches += 1;
    const consecutive = this._consecutiveCodecMismatches;

    console.warn(
      `[MSE Connection #${connectionId}] ${errorKind} detected${enriched ? " (enriched)" : ""} —` +
        ` immediate reconnect (no retry penalty), consecutive=${consecutive}`
    );
    diagnosticEventLog.record("mse:audio", "warn", errorKind, {
      connectionId,
      errorMessage: message,
      enriched: !!enriched,
      consecutiveCodecMismatches: consecutive,
    });

    // Server-side stream reset that we recover from seamlessly. Breadcrumb for
    // context on any *real* error that follows — but NOT a Sentry issue: firing
    // captureException here drowned NEWSQUAWK-PORTALS-13K with auto-recoveries.
    Sentry.addBreadcrumb({
      category: "mse",
      level: "warning",
      message: `MSE ${errorKind}${enriched ? " (enriched)" : ""} — auto-recovered`,
      data: {
        component: "MSEPlayerManager",
        connectionType: "mse",
        connectionId,
        address,
        errorMessage: message,
        consecutiveCodecMismatches: consecutive,
      },
    });

    // Clear stall timer and cooldown so the reconnect isn't delayed.
    if (this._stallTimeout) {
      clearTimeout(this._stallTimeout);
      this._stallTimeout = null;
    }
    this._lastStallReconnectTime = 0;

    // After repeated consecutive codec resets a bare reconnect keeps
    // re-negotiating the same wedged SourceBuffer against a stream that may
    // still be mid-reset. Swap the DOM element for a guaranteed-clean
    // MediaSource, then reset the counter so we don't re-request next attempt.
    if (
      consecutive >= MSEPlayerManager.CODEC_MISMATCH_FRESH_ELEMENT_THRESHOLD &&
      typeof this.callbacks.onRequestFreshElement === "function"
    ) {
      diagnosticEventLog.record("mse:audio", "warn", "fresh-element-requested", {
        connectionId,
        reason: "codec-mismatch",
        consecutiveCodecMismatches: consecutive,
      });
      Sentry.addBreadcrumb({
        category: "mse",
        level: "warning",
        message: "Requesting fresh audio element after consecutive codec mismatches",
        data: { connectionId, consecutiveCodecMismatches: consecutive },
      });
      try {
        this.callbacks.onRequestFreshElement();
      } catch (cbErr) {
        console.warn(
          `[MSE Connection #${connectionId}] onRequestFreshElement threw:`,
          cbErr
        );
      }
      this._consecutiveCodecMismatches = 0;
    }

    if (this.isActive() && onError) {
      const recoverable = new Error(message);
      recoverable._skipRetryCount = true;
      onError(recoverable);
    }
    this.disconnect();
  }

  /**
   * Disconnect and clean up the player
   * This is the critical function that must execute properly to prevent SourceBuffer errors
   */
  async disconnect() {
    // If already disconnecting, wait for that to complete
    if (this.disconnectPromise) {
      return this.disconnectPromise;
    }

    // Create and store the disconnect promise
    this.disconnectPromise = this._internalDisconnect();

    try {
      await this.disconnectPromise;
    } finally {
      this.disconnectPromise = null;
    }
  }

  /**
   * Internal disconnect implementation
   */
  async _internalDisconnect() {
    // Prevent double cleanup
    if (this.state === "stopping" || this.state === "destroyed") {
      return;
    }

    const previousState = this.state;
    const previousConnectionId = this.activeConnectionId;
    this.state = "stopping";

    console.log(`[MSE Disconnect] Stopping connection #${previousConnectionId} (previous state: ${previousState})`);
    diagnosticEventLog.record("mse:audio", "info", "disconnect-start", { connectionId: previousConnectionId, previousState });

    // Clear active connection ID to ignore any lingering callbacks
    this.activeConnectionId = null;
    this._stopBufferTrimming();
    this._stopExternalPauseDetection();
    if (this._stallTimeout) {
      clearTimeout(this._stallTimeout);
      this._stallTimeout = null;
    }

    try {
      if (this.player) {
        // CRITICAL: Stop the player FIRST
        // This prevents the player's internal interval from trying to access SourceBuffer
        try {
          this.player.stop();
          console.log(`[MSE Disconnect] Player stopped for connection #${previousConnectionId}`);
        } catch (e) {
          console.debug(`[MSE Disconnect] Error stopping MSE player for connection #${previousConnectionId}:`, e);
        }

        // Give the player time to fully close WebSocket and stop all internal operations
        // This prevents race conditions where a new connection starts before the old WebSocket closes
        // Delay is configurable via MSEPlayerManager.DISCONNECT_DELAY_MS
        await new Promise((resolve) => setTimeout(resolve, MSEPlayerManager.DISCONNECT_DELAY_MS));

        // Now destroy the player
        // The player will handle its own MediaSource cleanup
        try {
          this.player.destroy();
        } catch (e) {
          console.debug("Error destroying MSE player:", e);
        }

        this.player = null;
      }

      // Fully detach the media element so the previous MediaSource is released.
      // Setting src="" is NOT a full detach on Safari — it triggers a load with a
      // synthetic empty URL and the element stays in NETWORK_LOADING/HAVE_NOTHING.
      // The next reconnect then attaches a new MediaSource to a wedged element and
      // never receives an init segment, producing the mse-no-data-error death spiral
      // (Sentry Issue NEWSQUAWK-PORTALS-17A). Using removeAttribute + load() forces
      // the resource selection algorithm to abort and reset to NETWORK_EMPTY.
      if (this.mediaElement) {
        try {
          // Pause first so the element stops trying to consume the dying source.
          if (!this.mediaElement.paused) {
            this.mediaElement.pause();
          }

          // Revoke the previous blob URL — Flussonic creates one per session via
          // URL.createObjectURL(mediaSource). Without revocation it leaks.
          const oldSrc = this.mediaElement.src;
          if (oldSrc && oldSrc.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(oldSrc);
            } catch {
              /* noop */
            }
          }

          // removeAttribute is stronger than src="" on Safari/WebKit.
          this.mediaElement.removeAttribute("src");
          this.mediaElement.srcObject = null;

          // Force the resource selection algorithm to abort and reset to
          // NETWORK_EMPTY. This is what releases Safari's hold on the previous
          // MediaSource so the next retry starts from a clean state.
          this.mediaElement.load();
        } catch (e) {
          console.debug("Error clearing media element:", e);
        }
      }
    } catch (e) {
      console.warn("Error during MSE player cleanup:", e);
      diagnosticEventLog.record("mse:audio", "warn", "cleanup-error", { error: e instanceof Error ? e.message : String(e) });

      // Only log to Sentry if this was an unexpected cleanup
      if (previousState === "connected" || previousState === "connecting") {
        Sentry.captureException(e, {
          tags: {
            component: "MSEPlayerManager",
            context: "mse_cleanup_error",
            connection_type: "mse",
          },
          extra: {
            previousState,
            address: this.address,
          },
          fingerprint: ["MSEPlayerManager", "cleanup"],
        });
      }
    } finally {
      diagnosticEventLog.record("mse:audio", "info", "disconnect-complete", { previousState });
      this.state = "idle";
      this.mediaElement = null;
      this.address = null;
      this.callbacks = { onSuccess: null, onError: null, onRequestFreshElement: null };
    }
  }

  /**
   * Start periodic buffer trimming to prevent QuotaExceededError on long sessions.
   * Nudges currentTime forward if the buffer falls too far behind live edge.
   */
  _startBufferTrimming() {
    this._stopBufferTrimming();
    this.bufferTrimInterval = setInterval(() => {
      try {
        if (!this.mediaElement || this.state !== "connected") {
          return;
        }
        const buffered = this.mediaElement.buffered;
        if (buffered.length > 0) {
          const end = buffered.end(buffered.length - 1);
          // If currentTime is more than 5s behind the live edge, seek to near-live
          if (end - this.mediaElement.currentTime > 5) {
            this.mediaElement.currentTime = end - 1.5;
          }
        }
      } catch (e) {
        // SourceBuffer may have been detached — stop trimming silently
        console.debug("[MSE BufferTrim] Error accessing buffered:", e);
        this._stopBufferTrimming();
      }
    }, 10000);
  }

  _stopBufferTrimming() {
    if (this.bufferTrimInterval) {
      clearInterval(this.bufferTrimInterval);
      this.bufferTrimInterval = null;
    }
  }

  resetStallCooldown() {
    this._lastStallReconnectTime = 0;
  }

  /**
   * Detect browser power-saving pauses on the <video> element.
   * Chrome pauses "video-only background media" when the tab is hidden.
   * If detected, attempt an immediate play() resume.
   */
  _startExternalPauseDetection(audioElement, connectionId) {
    this._stopExternalPauseDetection();
    this._handleExternalPause = () => {
      if (
        document.visibilityState === "hidden" &&
        this.state === "connected" &&
        this.activeConnectionId === connectionId
      ) {
        console.warn(`[MSE Connection #${connectionId}] External pause detected in background — attempting resume`);
        diagnosticEventLog.record("mse:audio", "warn", "external-pause-resume", { connectionId });
        audioElement.play().catch((err) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          console.warn(`[MSE Connection #${connectionId}] Resume after external pause failed:`, err);
          const errMsg = err instanceof Error ? err.message : String(err);
          const resumeDiagnostics = getPlayerFailureDiagnostics(
            this.address ?? "unknown",
            "mse",
            errMsg,
            this._snapshotMediaState()
          );
          Sentry.captureException(
            err instanceof Error ? err : new Error(errMsg),
            {
              tags: buildPlayerFailureTags(
                "MSEPlayerManager",
                "mse_external_pause_resume_failed",
                "mse",
                resumeDiagnostics
              ),
              extra: {
                connectionId,
                address: this.address,
                networkDiagnostics: resumeDiagnostics.network,
              },
              fingerprint: ["MSEPlayerManager", "external-pause-resume-failed"],
            }
          );
        });
      }
    };
    audioElement.addEventListener("pause", this._handleExternalPause);
  }

  _stopExternalPauseDetection() {
    if (this.mediaElement && this._handleExternalPause) {
      this.mediaElement.removeEventListener("pause", this._handleExternalPause);
    }
    this._handleExternalPause = null;
  }

  /**
   * Capture a snapshot of the HTMLMediaElement state for diagnostics.
   * Called when Flussonic passes empty or uninformative errors.
   */
  _snapshotMediaState() {
    if (!this.mediaElement) {
      return { available: false };
    }

    const el = this.mediaElement;
    const networkLabels = ["NETWORK_EMPTY", "NETWORK_IDLE", "NETWORK_LOADING", "NETWORK_NO_SOURCE"];
    const readyLabels = ["HAVE_NOTHING", "HAVE_METADATA", "HAVE_CURRENT_DATA", "HAVE_FUTURE_DATA", "HAVE_ENOUGH_DATA"];

    const snapshot = {
      available: true,
      networkState: networkLabels[el.networkState] || `UNKNOWN_${el.networkState}`,
      readyState: readyLabels[el.readyState] || `UNKNOWN_${el.readyState}`,
      paused: el.paused,
      ended: el.ended,
      currentTime: el.currentTime,
      error: null,
      bufferedRanges: [],
      managerState: this.state,
      connectionId: this.activeConnectionId,
    };

    if (el.error) {
      snapshot.error = {
        code: el.error.code,
        message: el.error.message || null,
      };
    }

    try {
      for (let i = 0; i < el.buffered.length; i++) {
        snapshot.bufferedRanges.push({
          start: el.buffered.start(i).toFixed(2),
          end: el.buffered.end(i).toFixed(2),
        });
      }
    } catch {
      snapshot.bufferedRanges = "unavailable";
    }

    return snapshot;
  }

  /**
   * Set volume on the media element
   * @param {number} volume - Volume level (0-1)
   */
  setVolume(volume) {
    if (this.mediaElement && this.isActive()) {
      try {
        this.mediaElement.volume = volume;
      } catch (e) {
        console.debug("Error setting volume:", e);
      }
    }
  }

  /**
   * Destroy the manager completely
   */
  async destroy() {
    await this.disconnect();
    this.state = "destroyed";
  }
}

export default MSEPlayerManager;
